/**
 * @brisa/shared/observability/landing — wrappers canônicos para eventos da
 * feature landing-publica (METRICS_SPEC §3).
 *
 * Schema base canônico (AGR-2026-013 + _BASE_PROPS.md v1.1):
 *   user_id, session_id, feature_flag_active, release, timestamp, env
 *
 * Prop adicional desta feature: feature_landing_planos_active (boolean)
 *
 * Eventos cobertos:
 *   1. Landing Page Viewed
 *   2. Plan Card Clicked
 *   3. Club Card Clicked
 *   4. Landing CTA Clicked
 *   5. Locale Toggled (Landing)
 *   6. Map Loaded
 *   7. Map Marker Clicked
 */
import { gaEvent } from './ga';
import { clarityEvent } from './clarity';
import { logger } from '../logger';

/** Props base canônicas (AGR-2026-013 + _BASE_PROPS.md v1.1) */
export interface BaseLandingEventProps {
  user_id: string;           // UUID autenticado OU sentinel 'anonymous' / 'authenticated'
  session_id: string;        // cookie brisa_sid ou 'unknown' (race da 1ª visita SSG)
  feature_flag_active: boolean;       // getLandingFlags().mainOn
  feature_landing_planos_active: boolean; // getLandingFlags().plansOn — específico desta feature
}

function withCanonical<T extends object>(
  props: T,
  base: BaseLandingEventProps,
): Record<string, unknown> {
  return {
    ...base,
    ...props,
    release: process.env.NEXT_PUBLIC_RELEASE ?? 'local',
    env: process.env.NEXT_PUBLIC_BRISA_ENV ?? process.env.BRISA_ENV ?? 'local',
    timestamp: Date.now(),
  };
}

/** Rotas canônicas da landing pública — usado para segmentação no GA4 (METRICS_SPEC §3.2). */
export type LandingRoute = 'home' | 'pass' | 'clubes' | 'club_detail' | 'clubes_map';

// ─────────────────────────────────────────────
// Evento 1 — Landing Page Viewed
// ─────────────────────────────────────────────
export function trackLandingPageViewed(
  props: {
    path: string;
    route: LandingRoute;
    locale: 'pt' | 'es';
    referrer: string | null;
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
  },
  base: BaseLandingEventProps,
): void {
  const fullProps = withCanonical(props, base);
  gaEvent('landing_page_viewed', fullProps);
  clarityEvent('landing_page_viewed', fullProps);
  logger.info('Landing Page Viewed', fullProps);
}

// ─────────────────────────────────────────────
// Evento 2 — Plan Card Clicked (FLP=ON apenas)
// ─────────────────────────────────────────────
export type PlanKey = 'visitor_week' | 'basic' | 'premium4' | 'premium10' | 'unlimited';

export function trackPlanCardClicked(
  props: {
    plan_key: PlanKey;
    surface: string;
    cta_destination: 'waitlist';
  },
  base: BaseLandingEventProps,
): void {
  const fullProps = withCanonical(props, base);
  gaEvent('plan_card_clicked', fullProps);
  clarityEvent('plan_card_clicked', fullProps);
  logger.info('Plan Card Clicked', fullProps);
}

// ─────────────────────────────────────────────
// Evento 3 — Club Card Clicked
// ─────────────────────────────────────────────
export function trackClubCardClicked(
  props: {
    club_id: string;
    club_slug: string;
    integration_status: 'listed' | 'partner';
    surface: string;
  },
  base: BaseLandingEventProps,
): void {
  const fullProps = withCanonical(props, base);
  gaEvent('club_card_clicked', fullProps);
  clarityEvent('club_card_clicked', fullProps);
  logger.info('Club Card Clicked', fullProps);
}

// ─────────────────────────────────────────────
// Evento 4 — Landing CTA Clicked
// ─────────────────────────────────────────────
export function trackLandingCtaClicked(
  props: {
    cta_label: string;
    cta_destination: 'waitlist';
    from: string;
    surface: string;
    surface_granular: string;
  },
  base: BaseLandingEventProps,
): void {
  const fullProps = withCanonical(props, base);
  gaEvent('landing_cta_clicked', fullProps);
  clarityEvent('landing_cta_clicked', fullProps);
  logger.info('Landing CTA Clicked', fullProps);
}

// ─────────────────────────────────────────────
// Evento 5 — Locale Toggled (Landing)
// ─────────────────────────────────────────────
export function trackLocaleToggled(
  props: {
    from_locale: 'pt' | 'es';
    to_locale: 'pt' | 'es';
    path: string;
  },
  base: BaseLandingEventProps,
): void {
  const fullProps = withCanonical(props, base);
  gaEvent('landing_locale_toggled', fullProps);
  clarityEvent('landing_locale_toggled', fullProps);
  logger.info('Locale Toggled (Landing)', fullProps);
}

// ─────────────────────────────────────────────
// Evento 6 — Map Loaded
// ─────────────────────────────────────────────
export function trackMapLoaded(
  props: {
    markers_count: number;
    mapbox_token_active: boolean;
  },
  base: BaseLandingEventProps,
): void {
  const fullProps = withCanonical(props, base);
  gaEvent('landing_map_loaded', fullProps);
  clarityEvent('landing_map_loaded', fullProps);
  logger.info('Map Loaded', fullProps);
}

// ─────────────────────────────────────────────
// Evento 7 — Map Marker Clicked
// ─────────────────────────────────────────────
export function trackMapMarkerClicked(
  props: {
    club_id: string;
    club_slug: string;
    integration_status: 'listed' | 'partner';
  },
  base: BaseLandingEventProps,
): void {
  const fullProps = withCanonical(props, base);
  gaEvent('landing_map_marker_clicked', fullProps);
  clarityEvent('landing_map_marker_clicked', fullProps);
  logger.info('Map Marker Clicked', fullProps);
}
